import React, { useEffect, useRef, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { CreditCard, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { createPageUrl } from '@/utils';
import { persistBookingWithRetry } from '@/utils/persistBookingWithRetry';
import { deferaCapitalNmiUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { awaitCapitalCheckoutConfirmed, deriveCapitalTripReference } from '@/lib/awaitCapitalCheckout';
import { nmiDeclineMessage } from '@/lib/nmiDeclineMessage';
import { isBookingGateRefusal } from '../../../supabase/functions/_shared/booking-blacklist-gate.js';
import { extractNmiCardDisplayFromCollectJs } from '@/utils/nmiCardDisplay';
import { recordProcessorMismatch, processorMismatchToast } from '@/lib/processorMismatchRecovery';
import BillingAddressFields, {
  EMPTY_BILLING_ADDRESS,
  validateBillingAddress,
} from '@/components/defera-capital-nmi/BillingAddressFields';
import {
  NMI_FIELD_SHELL_CLASS,
  nmiCollectParentStyles,
  nmiCollectStyleConfig,
} from '@/components/defera-capital-nmi/nmiCollectFieldStyles';
import CheckoutPaymentTrustFooter from '@/components/booking/CheckoutPaymentTrustFooter';

/**
 * Defera Capital — NMI / Vector Payments checkout form.
 *
 * Parallel to `DeferaZeroCheckoutForm` (Stripe). Mounted by `Review.jsx` when
 * `platform_settings.defera_capital_processor === 'nmi'`. Stripe form is NOT
 * touched and remains the default path.
 *
 * Card collection uses NMI's Collect.js inline mode (hosted iframe fields).
 * The card data never touches our server or page DOM — we only see a one-time
 * `payment_token` which gets posted to `defera-capital-nmi/create-checkout`.
 * On the gateway's first successful charge the card is added to the NMI
 * Customer Vault; subsequent installments fire via our cron against that
 * vault id (no card data, no Collect.js, no customer interaction).
 *
 * Props mirror `DeferaZeroCheckoutForm` so Review.jsx can branch without
 * reshaping any state.
 */

const COLLECT_JS_BASE = (typeof window !== 'undefined' && window.NMI_API_BASE_URL)
  ? window.NMI_API_BASE_URL
  : 'https://vectorpayments.transactiongateway.com';

const COLLECT_JS_SCRIPT_ID = 'nmi-collect-js';

/**
 * Singleton script loader.
 *
 * Collect.js is loaded once per page and reused across mounts. If a caller
 * ever needs different data-* attributes (different tokenization key, etc.),
 * we detect the signature mismatch and reload the tag so Collect.js picks
 * up the new values — `CollectJS.configure()` cannot retroactively change
 * what the script tag advertised at boot time.
 */
function loadCollectJs(tokenizationKey, dataAttrs = {}) {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  const existing = document.getElementById(COLLECT_JS_SCRIPT_ID);

  // Stable signature of all data attrs (incl. tokenization key). If the
  // previous load used different values, swap the tag.
  //
  // IMPORTANT: store the signature as a plain JS expando property on the
  // script element (`_deferaSig`), NOT via `dataset` / `data-*`. Collect.js
  // validates every `data-*` attribute on its own script tag with a strict
  // allowlist and throws `"You provided too many fields. Unexpected fields
  // for <fieldName>"` if it sees anything it doesn't recognise — which
  // silently aborts init and leaves `window.CollectJS` unset.
  const desiredSig = JSON.stringify({ tokenizationKey, ...dataAttrs });
  if (existing && existing._deferaSig !== desiredSig) {
    try { delete window.CollectJS; } catch { /* property may be non-configurable */ }
    existing.remove();
  }

  if (window.CollectJS && existing && existing._deferaSig === desiredSig) {
    return Promise.resolve(window.CollectJS);
  }

  const current = document.getElementById(COLLECT_JS_SCRIPT_ID);
  if (current) {
    return new Promise((resolve, reject) => {
      current.addEventListener('load', () => resolve(window.CollectJS), { once: true });
      current.addEventListener('error', () => reject(new Error('Collect.js failed to load')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = COLLECT_JS_SCRIPT_ID;
    s.src = `${COLLECT_JS_BASE}/token/Collect.js`;
    if (tokenizationKey) {
      s.setAttribute('data-tokenization-key', tokenizationKey);
    }
    for (const [k, v] of Object.entries(dataAttrs)) {
      if (v == null || v === false) continue;
      s.setAttribute(k, String(v));
    }
    // Plain JS property, NOT a `data-*` attribute — see comment at top
    // of this function for why Collect.js rejects unknown data attrs.
    s._deferaSig = desiredSig;
    s.async = true;

    // Diagnostic: log what we're actually asking Collect.js to do. When
    // `window.CollectJS` never appears, ops needs the full attribute list
    // + the script URL to figure out which attr (or the key) is wrong.
    // Snapshots the attrs at insert time so console isn't polluted with
    // a stale reference.
    try {
      const attrSnapshot = {};
      for (const attr of s.attributes) attrSnapshot[attr.name] = attr.value;
      console.info('[Collect.js] inserting script tag', {
        src: s.src,
        attrs: attrSnapshot,
      });
    } catch { /* console may be locked down in some embeds */ }
    /**
     * Collect.js sometimes exposes `window.CollectJS` a tick AFTER its
     * `onload` event (the bundle defers IIFE setup until after parse), so
     * blindly resolving with `window.CollectJS` from the load handler can
     * hand back `undefined`. Wait briefly for the global to appear before
     * either resolving or rejecting with a useful message.
     *
     * The "loaded but global never appeared" branch is almost always one
     * of: (a) the tokenization key is wrong / for a different gateway, or
     * (b) the calling origin isn't in NMI's Allowed Domains list for this
     * key. Mention these in the error so ops can self-diagnose.
     */
    s.onload = () => {
      console.info('[Collect.js] script onload fired', {
        hasCollectJS: Boolean(window.CollectJS),
        // Some NMI integrations expose a different global name during
        // initial boot — capture every uppercase identifier on window
        // matching /collect/i so we can spot it.
        windowCollectKeys: Object.keys(window).filter((k) => /collect/i.test(k)),
      });
      if (window.CollectJS) {
        resolve(window.CollectJS);
        return;
      }
      const startedAt = Date.now();
      const maxWaitMs = 2000;
      const tick = () => {
        if (window.CollectJS) {
          console.info('[Collect.js] CollectJS appeared after', Date.now() - startedAt, 'ms');
          resolve(window.CollectJS);
          return;
        }
        if (Date.now() - startedAt >= maxWaitMs) {
          console.error('[Collect.js] timed out waiting for window.CollectJS', {
            elapsedMs: Date.now() - startedAt,
            windowCollectKeys: Object.keys(window).filter((k) => /collect/i.test(k)),
            scriptStillInDom: Boolean(document.getElementById(COLLECT_JS_SCRIPT_ID)),
          });
          reject(new Error(
            'Collect.js loaded but window.CollectJS was never exposed. ' +
            'Usually means the tokenization key is missing the "Tokenization" ' +
            'permission, or the calling origin is not in NMI\'s Allowed ' +
            'Domains list for this key. Check the browser console for the ' +
            '[Collect.js] log entries directly above this error.',
          ));
          return;
        }
        setTimeout(tick, 100);
      };
      setTimeout(tick, 100);
    };
    s.onerror = (evt) => {
      console.error('[Collect.js] script onerror fired', evt);
      reject(new Error('Collect.js failed to load (network / CSP / blocked URL)'));
    };
    document.head.appendChild(s);
  });
}

export default function DeferaCapitalNmiCheckoutForm({
  totalAmountCents,
  holdFeeCents = 0,
  flightData,
  baseFareCents,
  serviceFeeCents,
  departureCity,
  arrivalCity,
  departureDate,
  returnDate,
  marketingAirline,
  cabinClass,
  tripType,
  passengers,
  onSuccess,
  onError,
  disabled,
  termsAccepted,
  paymentFrequency = 'weekly',
  // Defera Capital always uses slice-installments mode on the NMI track.
  depositCents = 0,
  subtotalBeforeHoldCents = 0,
  depositPercentage = 20,
  installmentCount = 0,
  installmentAmountCents = 0,
  clientPaymentSchedule = null,
  /** Faye travel insurance selection (null when none). The server re-prices
   *  it and rejects a mismatch. */
  insurance = null,
  /** Called when the server refuses that insurance (price changed, not
   *  available): Review takes the plan off and prices it again. */
  onInsuranceRefused,
  /** Called when the server couldn't hold the Defera Miles this checkout
   *  spends (the balance changed since the page loaded): Review reloads the
   *  balance and prices the trip again. Nothing was charged. */
  onMilesRefused,
  /** Rendered above the card details (Review's Defera Miles balance, shown
   *  like a payment method). */
  paymentMethodsTop = null,
  /**
   * How much Defera Credit the customer chose to spend on THIS checkout, in
   * cents. `null` means "all of it" — the default, and what every checkout did
   * before 2026-09-19. The server clamps to min(requested, balance).
   */
  deferaCreditMaxCents = null,
  /** Optional content rendered between payment methods and the confirm button */
  renderBetween = null,
  /**
   * Optional override for the public tokenization key. If omitted, the
   * form fetches it from `defera-capital-nmi/create-tokenization-session`
   * (which reads `NMI_TOKENIZATION_KEY` from Supabase Edge Function
   * secrets). Server-side resolution is the preferred path because it
   * keeps the key out of the client bundle and matches the pattern used
   * by the Update Card form — one place to configure the key, one source
   * of truth.
   */
  tokenizationKey: tokenizationKeyProp,
}) {
  /**
   * Tokenization key. Fetched from the server on mount; falls back to the
   * legacy `tokenizationKey` prop only if explicitly provided. Lives in
   * state so the configure effect can wait for it before booting Collect.js.
   */
  const [tokenizationKey, setTokenizationKey] = useState(tokenizationKeyProp || null);
  /** True while we're fetching the tokenization key from the Edge Function. */
  const [tokenSessionLoading, setTokenSessionLoading] = useState(!tokenizationKeyProp);
  const [collectReady, setCollectReady] = useState(false);
  const [collectError, setCollectError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  /**
   * Billing address. Required by NMI when the merchant account has AVS
   * fields turned on (Settings → Security → AVS in Vector Payments). We
   * collect it client-side, validate before tokenization, and forward to
   * the create-checkout endpoint which passes it straight to NMI's
   * Payment API.
   */
  const [billingAddress, setBillingAddress] = useState(EMPTY_BILLING_ADDRESS);
  const [billingErrors, setBillingErrors] = useState({});
  /** Sync guard to block double-submits before React state catches up. */
  const submitLockRef = useRef(false);
  /** The current payment token (single-use) once Collect.js fires its callback. */
  const pendingTokenRef = useRef(null);
  /** Stable callback Collect.js calls when it has a token (matches `submitTokenCallback`). */
  const onTokenRef = useRef(null);

  /**
   * Fetch the public tokenization key from the server.
   *
   * Why server-side: keeping the key in Supabase Edge Function env
   * (`NMI_TOKENIZATION_KEY`) means only one place to configure NMI and
   * removes the failure mode where `VITE_NMI_TOKENIZATION_KEY` is missing
   * from a deployment's client bundle. The Update Card form already
   * follows this pattern — we mirror it here for consistency.
   *
   * Skip the fetch entirely if a key was passed in via prop (legacy
   * compat — Review.jsx may still pass `import.meta.env.VITE_...`).
   */
  useEffect(() => {
    if (tokenizationKeyProp) {
      setTokenizationKey(tokenizationKeyProp);
      setTokenSessionLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Please sign in to continue.');
        }
        const res = await fetch(deferaCapitalNmiUrl('create-tokenization-session'), {
          method: 'POST',
          headers: mergeSupabaseEdgeHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          }),
          body: '{}',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.tokenization_key) {
          throw new Error(
            data?.error
              || `Could not start card collection (HTTP ${res.status}). ` +
                 'Set NMI_TOKENIZATION_KEY in Supabase Edge Function secrets.',
          );
        }
        if (!cancelled) {
          setTokenizationKey(data.tokenization_key);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('NMI tokenization-session fetch failed', e);
          setCollectError(e?.message || 'Could not start card collection.');
        }
      } finally {
        if (!cancelled) setTokenSessionLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tokenizationKeyProp]);

  // Configure Collect.js once after the script loads.
  useEffect(() => {
    let cancelled = false;
    if (!tokenizationKey) {
      // We're either waiting on the server fetch (handled above with a
      // spinner) or it failed and `collectError` is already set. Don't
      // clobber that error from this effect.
      return undefined;
    }
    // Clear any error from a previous attempt — the deps that re-trigger
    // this effect (deposit/installment amounts) may have just become
    // valid, and we want the customer to see card fields appear instead
    // of a stale "Could not load card collection" message.
    setCollectError(null);
    setCollectReady(false);
    // Apple Pay is intentionally NOT plumbed through Collect.js. See the
    // module-level Apple Pay comment block above — we use NMI's hosted
    // Collect Checkout for Apple Pay instead, which doesn't require any
    // data-* attributes on the script tag. Passing `{}` here keeps
    // Collect.js's bootstrap focused on card-only fields.
    (async () => {
      try {
        const CollectJS = await loadCollectJs(tokenizationKey, {});
        if (cancelled) return;
        CollectJS.configure({
          variant: 'inline',
          styleSniffer: false,
          tokenizationKey,
          fields: {
            ccnumber: {
              selector: '#dc-nmi-ccnumber',
              title: 'Card Number',
              placeholder: 'Card number',
            },
            ccexp: {
              selector: '#dc-nmi-ccexp',
              title: 'Expiration',
              placeholder: 'MM / YY',
            },
            cvv: {
              display: 'show',
              selector: '#dc-nmi-cvv',
              title: 'CVV',
              placeholder: 'CVC',
            },
          },
          // customCss is applied INSIDE Collect.js's hosted iframes to
          // each field's <input>. The visible wrapper styling (rounded
          // glass background, 44px height, horizontal padding) lives on
          // the parent shell via NMI_FIELD_SHELL_CLASS.
          //
          // Centering strategy: `line-height: 44px` on the input expands
          // its line-box to 44px (matches the wrapper height). The
          // browser's native baseline math then places text glyphs and
          // the caret in the vertical middle of that line-box. This
          // works regardless of whether Collect.js's iframe body has 0,
          // 8, or any other margin, because the line-box itself fills
          // the iframe.
          //
          // We deliberately do NOT set `height` here — that would
          // override the line-box-driven height and re-introduce the
          // top-aligned glyph problem we just fought through.
          ...nmiCollectStyleConfig(),
          fieldsAvailableCallback: () => {
            if (!cancelled) setCollectReady(true);
          },
          callback: (response) => {
            /**
             * Collect.js's tokenization callback. The form is card-only;
             * the callback always lands here with `onTokenRef` set by
             * handleConfirm's startPaymentRequest. We keep the
             * unsolicited-fire guard as a safety net.
             */
            const inflight = onTokenRef.current;
            if (inflight) {
              inflight(response);
              return;
            }
            console.warn(
              'Collect.js tokenization fired without an inflight request. Dropping token.',
            );
          },
        });
      } catch (e) {
        if (!cancelled) {
          console.error('Collect.js init failed', e);
          // Surface the real error to help ops debug. The two common modes
          // are: (1) the script tag's onerror fired (network / CSP / CORS /
          // wrong URL — message is "Collect.js failed to load") and (2)
          // `CollectJS.configure()` threw because the tokenization key is
          // rejected by the gateway. The message text differentiates them.
          const detail = e?.message ? ` (${e.message})` : '';
          setCollectError(`Could not load card collection. Please refresh and try again.${detail}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenizationKey]);

  /**
   * Shared post-tokenization path. Once Collect.js hands us a `payment_token`,
   * the rest of the flow mirrors the Stripe variant exactly: POST create-checkout,
   * handle deferred-webhook redirect, handle booking_payload client-persist,
   * poll on ambiguous errors.
   */
  const processToken = useCallback(async ({ paymentToken, cardDisplay, session, idempotencyKey, billingAddress: billingAddressArg }) => {
    const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
    const storageKey = `defera_capital_nmi_idem:${session.user.id}:${flightKey}`;
    const clearIdemKey = () => {
      try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
    };

    const createBody = {
      // Omitted when they haven't narrowed it, so the request is byte-identical
      // to before and the server's "no cap" default applies.
      ...(Number.isFinite(deferaCreditMaxCents)
        ? { defera_credit_max_cents: Math.max(0, Math.round(deferaCreditMaxCents)) }
        : {}),
      flight_data: flightData,
      total_amount_cents: totalAmountCents,
      hold_fee_cents: holdFeeCents,
      base_fare_cents: baseFareCents,
      service_fee_cents: serviceFeeCents,
      payment_frequency: paymentFrequency,
      payment_token: paymentToken,
      departure_city: departureCity,
      arrival_city: arrivalCity,
      departure_date: departureDate,
      return_date: returnDate || null,
      marketing_airline: marketingAirline,
      cabin_class: cabinClass,
      trip_type: tripType,
      passengers: passengers,
      slice_installments_mode: true,
      deposit_cents: depositCents,
      subtotal_before_hold_cents: subtotalBeforeHoldCents,
      deposit_percentage: depositPercentage,
      installment_count: installmentCount,
      installment_amount_cents: installmentAmountCents,
      client_payment_schedule: clientPaymentSchedule,
      ...(insurance ? { insurance } : {}),
      // Billing address — required by NMI when AVS fields are on. Passed
      // through to `chargeInitialDeposit` server-side. The argument form
      // (`billingAddressArg`) lets the Apple Pay wallet path swap in the
      // address returned from Apple's payment sheet without colliding with
      // the card-entry path's React state.
      billing_address: billingAddressArg || null,
      ...(cardDisplay ? { card_display: cardDisplay } : {}),
      ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    };

    const res = await fetch(deferaCapitalNmiUrl('create-checkout'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(createBody),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      // Server-side processor guard kicked us back — the platform toggle flipped
      // to Stripe (or this trip was already settled on Stripe) while the NMI
      // form was open. Reload so the freshly-mounted hook renders the Stripe
      // form. No charge was attempted. The circuit breaker prevents an
      // infinite reload loop if the toggle and frontend stay out of sync.
      if (res.status === 409 && data?.code === 'PROCESSOR_MISMATCH') {
        const tripRef = deriveCapitalTripReference(flightData?.id || flightData?.offer_id) || 'pre_trip';
        const { shouldAutoReload } = recordProcessorMismatch(tripRef);
        const { message, description } = processorMismatchToast(shouldAutoReload);
        toast.error(message, description ? { description } : undefined);
        if (shouldAutoReload) {
          setTimeout(() => window.location.reload(), 1200);
        }
        onError?.(new Error('Processor mismatch'));
        return;
      }
      // Idempotency-key collision — same recovery as Stripe path.
      if (data?.error_type === 'idempotency_error') {
        clearIdemKey();
        toast.error(data.error || 'Please refresh the page and try again — your card was not charged.');
        onError?.(new Error('idempotency_error'));
        return;
      }
      // The booking gate refused before anything was charged (a restricted
      // traveler, or it couldn't screen). Terminal: there is nothing to poll
      // for, and "your payment is processing" would be untrue. Its message is
      // written for the customer as-is.
      if (isBookingGateRefusal(data)) {
        clearIdemKey();
        toast.error(data.error);
        onError?.(new Error(data.code));
        return;
      }
      // Refused by the server's integrity check before anything was charged
      // or saved: no "processing" poll, which could only find an earlier
      // attempt's row and send the customer to the status page.
      if (data?.error_type === 'checkout_integrity') {
        clearIdemKey();
        toast.error(data.error || 'We couldn’t confirm the price for this trip. Please refresh the page and try again.');
        onError?.(new Error('checkout_integrity'));
        return;
      }
      // The server refused the travel insurance before anything was charged
      // (its price changed, or it isn't available): no poll, the server's own
      // words, and Review takes the plan off to be added again or skipped.
      if (typeof data?.error_type === 'string' && data.error_type.startsWith('insurance_')) {
        clearIdemKey();
        toast.error(data.error || 'We couldn’t add the travel insurance. Please review it and try again.', {
          description: 'We took the plan off your trip. Add it again to see today’s price, or continue without it.',
        });
        onInsuranceRefused?.(data);
        onError?.(new Error(data.error_type));
        return;
      }
      // Defera Miles: the balance changed between this page and the payment,
      // so the server refused before charging anything. No poll; Review
      // reloads the balance and re-prices the trip.
      if (data?.error_type === 'miles_unavailable') {
        clearIdemKey();
        toast.error(data.error || 'Your Miles balance changed since this page loaded. Please refresh and try again.');
        onMilesRefused?.(data);
        onError?.(new Error('miles_unavailable'));
        return;
      }
      // Card decline: terminal, no money moved. Free idem key for next attempt.
      const looksLikeCardDecline =
        data?.error_type === 'card_error' || data?.gateway_response === '2';
      if (looksLikeCardDecline) {
        clearIdemKey();
      }
      // Ambiguous failure: poll checkout-status. The NMI webhook may have
      // landed the booking already (same recovery as Stripe path).
      const tripRef = deriveCapitalTripReference(flightData?.id || flightData?.offer_id);
      if (!looksLikeCardDecline && tripRef && session?.access_token) {
        toast.info('Your payment is processing — confirming with our system…', { duration: 5000 });
        try {
          const result = await awaitCapitalCheckoutConfirmed({
            tripReference: tripRef,
            accessToken: session.access_token,
            options: {
              statusUrl: deferaCapitalNmiUrl('checkout-status'),
              timeoutMs: 30_000,
            },
          });
          if (result.status === 'confirmed') {
            toast.success('Your trip is confirmed — taking you to My Trips.', { duration: 8000 });
            onSuccess?.({ success: true, recovered: true, trip_reference: tripRef });
            clearIdemKey();
            window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
            return;
          }
          if (result.status === 'processing') {
            toast.success(
              `Your deposit was received. We're finalizing your trip now — it should appear in My Trips within a minute. Reference: ${tripRef}`,
              { duration: 25000 },
            );
            onSuccess?.({ success: true, defer_booking_to_webhook: true, trip_reference: tripRef });
            window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
            return;
          }
          if (result.status === 'pending' || result.status === 'timeout') {
            toast.info(
              `We're still confirming your payment. We'll keep checking — reference ${tripRef}.`,
              { duration: 12000 },
            );
            onError?.(new Error('Checkout still pending after poll deadline'));
            /** `processor=nmi` tells the recovery page to poll the NMI status
             *  endpoint instead of the Stripe default. Without it the page
             *  would never find the pending row sitting in
             *  `defera_capital_nmi_pending_checkouts`. */
            window.location.href =
              `${createPageUrl('CapitalCheckoutStatus')}?ref=${encodeURIComponent(tripRef)}&processor=nmi`;
            return;
          }
        } catch (pollErr) {
          console.warn('checkout-status poll threw, falling back to error toast', pollErr);
        }
      }
      // Translate the gateway's raw response into a customer-friendly toast.
      // Keep the unvarnished vendor string in the console (and the rejected
      // Error) so engineers can grep logs / Sentry for NMI's exact text.
      const friendly = nmiDeclineMessage(data, {
        defaultMessage: 'We couldn\'t complete your booking. Please try again.',
      });
      console.error('[defera-capital-nmi] checkout error', {
        rawText: friendly.dev,
        error_type: data?.error_type,
        gateway_response: data?.gateway_response,
        gateway_response_code: data?.gateway_response_code,
        nmi_transaction_id: data?.nmi_transaction_id,
        detail: data?.detail,
      });
      toast.error(friendly.customer, {
        duration: friendly.severity === 'temporary' ? 8000 : 10000,
        description: friendly.action || undefined,
      });
      onError?.(new Error(friendly.dev));
      return;
    }

    if (!data.success) {
      const friendly = nmiDeclineMessage(data, {
        defaultMessage: 'We couldn\'t complete your booking. Please try again.',
      });
      console.error('[defera-capital-nmi] checkout returned non-success', {
        rawText: friendly.dev,
        data,
      });
      toast.error(friendly.customer, {
        description: friendly.action || undefined,
      });
      onError?.(new Error(friendly.dev));
      return;
    }

    // Happy path: NMI is synchronous, so the edge function materializes the
    // bookings row inline before responding. `booking_id` is present whenever
    // the inline write landed (typical case). The customer goes straight to
    // Trips with no polling.
    if (data.booking_id) {
      toast.success(
        `Your trip is booked. Reference ${data.trip_reference || 'pending'}.`,
        { duration: 12000 },
      );
      onSuccess?.(data);
      clearIdemKey();
      window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
      return;
    }

    // Fallback path: the deposit charged but the inline materialization
    // didn't land (rare — network blip between gateway response and our
    // bookings INSERT). The webhook safety net will write the row shortly,
    // so we route through the polling/recovery page like before.
    if (data.defer_booking_to_webhook) {
      toast.success(
        "Your deposit was received. We're confirming your trip now — it should appear in My Trips within a minute.",
        { duration: 14000 },
      );
      onSuccess?.(data);
      clearIdemKey();
      window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
      return;
    }

    // Synchronous booking path (server returned a booking_payload to persist client-side).
    const payload = data.booking_payload;
    if (!payload || typeof payload.user_id !== 'string') {
      toast.error(
        `Your payment was received. We're finalizing your trip now — it should appear in My Trips within a few minutes. If it doesn't, send us a message at defera.app/contact with reference ${data?.trip_reference || 'your trip'}.`,
        { duration: 20000 },
      );
      onError?.(new Error('Missing booking_payload'));
      return;
    }

    const bookingResult = await persistBookingWithRetry(session.access_token, payload);
    if (!bookingResult.ok) {
      toast.error(
        `Your payment was received. We're finalizing your trip now — it should appear in My Trips within a few minutes. If it doesn't, send us a message at defera.app/contact with reference ${payload.trip_reference || payload.booking_reference || 'your trip'}.`,
        { duration: 25000 },
      );
      onError?.(new Error(bookingResult.error));
      return;
    }

    clearIdemKey();
    onSuccess?.(data);
    window.location.href = createPageUrl('Dashboard') + '?defera_capital=success';
  }, [
    flightData,
    totalAmountCents,
    holdFeeCents,
    baseFareCents,
    serviceFeeCents,
    paymentFrequency,
    departureCity,
    arrivalCity,
    departureDate,
    returnDate,
    marketingAirline,
    cabinClass,
    tripType,
    passengers,
    depositCents,
    subtotalBeforeHoldCents,
    depositPercentage,
    installmentCount,
    installmentAmountCents,
    clientPaymentSchedule,
    insurance,
    onInsuranceRefused,
    onMilesRefused,
    onSuccess,
    onError,
  ]);

  const handleConfirm = useCallback(async () => {
    if (submitLockRef.current) return;
    if (!termsAccepted || isProcessing || disabled) return;
    if (!collectReady) {
      toast.error('Card form is still loading — try again in a moment.');
      return;
    }
    if (depositCents < 0) {
      toast.error('Invalid deposit amount. Please refresh and try again.');
      return;
    }
    if (depositCents >= 100 && (!installmentCount || installmentCount < 1 || !installmentAmountCents)) {
      toast.error('Payment schedule is not ready. Please confirm your deposit and plan above, or pick another departure date.');
      return;
    }
    // Billing address validation. We block the Collect.js tokenization call
    // entirely when the address is missing/invalid — sending a bad address
    // to NMI burns the order id (idempotency key) and the customer would
    // have to re-enter their card. Better to surface the error inline.
    const addrErrors = validateBillingAddress(billingAddress);
    if (Object.keys(addrErrors).length > 0) {
      setBillingErrors(addrErrors);
      toast.error('Please complete your billing address.');
      return;
    }
    setBillingErrors({});

    flushSync(() => {
      submitLockRef.current = true;
      setIsProcessing(true);
    });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in to continue');
        onError?.();
        return;
      }

      let idempotencyKey = null;
      try {
        const flightKey = flightData?.id || flightData?.offer_id || 'unknown';
        const storageKey = `defera_capital_nmi_idem:${session.user.id}:${flightKey}`;
        idempotencyKey = sessionStorage.getItem(storageKey);
        if (!idempotencyKey) {
          idempotencyKey = crypto.randomUUID();
          sessionStorage.setItem(storageKey, idempotencyKey);
        }
      } catch {
        idempotencyKey = crypto.randomUUID();
      }

      // Tokenize via Collect.js. The callback fires once with `{ token, card, ... }`.
      const tokenResp = await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          onTokenRef.current = null;
          reject(new Error('Card tokenization timed out. Please re-enter and try again.'));
        }, 30_000);
        onTokenRef.current = (response) => {
          clearTimeout(timeout);
          onTokenRef.current = null;
          if (response && response.token) {
            resolve(response);
          } else {
            reject(new Error(response?.message || 'Card tokenization failed. Please check your card details.'));
          }
        };
        try {
          window.CollectJS.startPaymentRequest();
        } catch (e) {
          clearTimeout(timeout);
          onTokenRef.current = null;
          reject(e);
        }
      });

      pendingTokenRef.current = tokenResp.token;
      await processToken({
        paymentToken: tokenResp.token,
        cardDisplay: extractNmiCardDisplayFromCollectJs(tokenResp),
        session,
        idempotencyKey,
        billingAddress,
      });
    } catch (err) {
      console.error('Defera Capital NMI checkout error:', err);
      const msg = err?.message || 'Something went wrong';
      toast.error(msg);
      onError?.(err);
    } finally {
      submitLockRef.current = false;
      setIsProcessing(false);
    }
  }, [
    termsAccepted,
    isProcessing,
    disabled,
    collectReady,
    depositCents,
    installmentCount,
    installmentAmountCents,
    flightData,
    onError,
    processToken,
    billingAddress,
  ]);

  if (collectError) {
    return (
      <>
        {/* The Miles card stays usable: Miles that pay the whole trip need no card. */}
        {paymentMethodsTop && <div className="mb-6">{paymentMethodsTop}</div>}
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
          {collectError}
        </div>
      </>
    );
  }

  // While we wait on the server tokenization-session response, show a
  // skeleton spinner — the customer shouldn't see the card form with no
  // iframes inside, which looks broken even though it's just loading.
  if (tokenSessionLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-transparent p-6 sm:p-8 shadow-[0_0_24px_-8px_rgba(0,0,0,0.3)]">
          {paymentMethodsTop && <div className="mb-6">{paymentMethodsTop}</div>}
          <div className="flex items-center justify-center py-10 text-sm text-white/60 gap-3">
            <Loader2 className="w-4 h-4 animate-spin" />
            Preparing secure card collection…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <style>{nmiCollectParentStyles('#dc-nmi-ccnumber, #dc-nmi-ccexp, #dc-nmi-cvv')}</style>

      {/*
        Container styling mirrors the Stripe form's gradient card. The
        `paymentCardGradient` import is a class-name dictionary (each key is
        a Tailwind class string), NOT a CSS style object, so we apply the
        gradient via className instead of `style=`.
      */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] via-white/[0.03] to-transparent p-6 sm:p-8 shadow-[0_0_24px_-8px_rgba(0,0,0,0.3)]">

        {/* Set aside while a payment is in flight: changing the Miles then
            wouldn't change what that request spends. */}
        {paymentMethodsTop && (
          <div
            className={`mb-6 transition-opacity ${isProcessing ? 'opacity-60 pointer-events-none' : ''}`}
            inert={isProcessing ? '' : undefined}
            aria-busy={isProcessing || undefined}
          >
            {paymentMethodsTop}
          </div>
        )}
        <div className="flex items-center gap-2 mb-6">
          <CreditCard className="w-5 h-5 text-white/70" />
          <h3 className="text-white text-sm font-normal tracking-wide uppercase">Card details</h3>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-white/60 font-normal mb-1.5 block">Card number</label>
            <div className={NMI_FIELD_SHELL_CLASS}>
              <div id="dc-nmi-ccnumber" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60 font-normal mb-1.5 block">Expiration</label>
              <div className={NMI_FIELD_SHELL_CLASS}>
                <div id="dc-nmi-ccexp" />
              </div>
            </div>
            <div>
              <label className="text-xs text-white/60 font-normal mb-1.5 block">CVV</label>
              <div className={NMI_FIELD_SHELL_CLASS}>
                <div id="dc-nmi-cvv" />
              </div>
            </div>
          </div>
          {!collectReady && (
            <p className="text-xs text-white/40 mt-2 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading secure card fields…
            </p>
          )}
        </div>

        <BillingAddressFields
          values={billingAddress}
          onChange={(next) => {
            setBillingAddress(next);
            // Clear inline error for any field the customer is actively
            // editing — re-validated on submit so we never block on a
            // value the customer is mid-keystroke fixing.
            if (Object.keys(billingErrors).length > 0) setBillingErrors({});
          }}
          errors={billingErrors}
          disabled={isProcessing}
          idPrefix="dc-nmi-billing"
        />

        {renderBetween && (
          <div className="mt-8 pt-6">{renderBetween}</div>
        )}

        <Button
          onClick={handleConfirm}
          disabled={disabled || isProcessing || !collectReady}
          className="w-full mt-6 h-12 rounded-2xl bg-white text-black hover:bg-white/90 font-semibold tracking-wide"
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Confirming…
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              Confirm Booking
            </span>
          )}
        </Button>

        <p className="text-[11px] text-white/60 font-normal text-center mt-4 max-w-[15rem] mx-auto leading-snug">
          Future installments charge automatically on your schedule, and you can update your payment method or pay early at any time.
        </p>

        <CheckoutPaymentTrustFooter className="mt-6" />
      </div>
    </div>
  );
}
